import { useEffect, useState, type ReactNode } from 'react'
import { signOut } from 'firebase/auth'
import { auth } from '../firebase'
import { subscribeParent } from '../lib/db'
import type { Parent } from '../lib/types'
import type { NavDest } from '../lib/nav'

export default function Layout({
  children,
  title,
  onBack,
  action,
  onNavigate,
  isAdmin,
  onAdmin,
  onAccount,
  onDownloads,
}: {
  children: ReactNode
  title: string
  onBack?: () => void
  action?: ReactNode
  onNavigate: (dest: NavDest) => void
  isAdmin?: boolean
  onAdmin?: () => void
  onAccount?: () => void
  onDownloads?: () => void
}) {
  const [parent, setParent] = useState<Parent | null>(null)
  const uid = auth.currentUser?.uid

  useEffect(() => {
    if (!uid) return
    return subscribeParent(uid, setParent)
  }, [uid])

  return (
    <div className="flex min-h-full">
      <aside className="hidden w-64 shrink-0 flex-col bg-brand-800 text-white md:flex">
        <div className="flex items-center gap-3 px-5 py-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 text-xl">
            🛡️
          </div>
          <div>
            <p className="font-bold leading-tight">KidSafe</p>
            <p className="text-xs text-brand-200">Control Parental</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-3">
          <button
            onClick={() => onNavigate('home')}
            className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm text-brand-100 transition hover:bg-white/5"
          >
            <span>🏠</span> Inicio
          </button>
          <button
            onClick={() => onNavigate('activity')}
            className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm text-brand-100 transition hover:bg-white/5"
          >
            <span>📊</span> Actividad
          </button>
          <button
            onClick={() => onNavigate('apps')}
            className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm text-brand-100 transition hover:bg-white/5"
          >
            <span>📱</span> Aplicaciones
          </button>
          {onAccount && (
            <button
              onClick={onAccount}
              className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm text-brand-100 transition hover:bg-white/5"
            >
              <span>👤</span> Mi cuenta
            </button>
          )}
          {onDownloads && (
            <button
              onClick={onDownloads}
              className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm text-brand-100 transition hover:bg-white/5"
            >
              <span>📥</span> Descargas
            </button>
          )}
          {isAdmin && onAdmin && (
            <button
              onClick={onAdmin}
              className="mt-2 flex w-full items-center gap-3 rounded-lg border border-white/15 px-4 py-3 text-sm font-medium text-white transition hover:bg-white/10"
            >
              <span>🛡️</span> SuperAdmin
            </button>
          )}
        </nav>
        <div className="border-t border-white/10 p-4">
          {parent?.name && (
            <p className="mb-2 truncate text-sm font-medium text-white">{parent.name}</p>
          )}
          <button
            onClick={() => signOut(auth)}
            className="w-full rounded-lg border border-white/20 px-4 py-2 text-sm text-white transition hover:bg-white/10"
          >
            Cerrar sesión
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-6 py-4">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                onClick={onBack}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                ← Volver
              </button>
            )}
            <h1 className="text-lg font-bold text-slate-800">{title}</h1>
          </div>
          <div className="flex items-center gap-3">
            {parent?.name && (
              <span className="hidden text-sm text-slate-500 sm:block">
                Hola, <b className="text-slate-700">{parent.name}</b>
              </span>
            )}
            {action}
            {onDownloads && (
              <button
                type="button"
                onClick={onDownloads}
                className="md:hidden rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white"
              >
                📥 Descargar
              </button>
            )}
            <button
              type="button"
              onClick={() => signOut(auth)}
              className="md:hidden rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600"
            >
              Salir
            </button>
          </div>
        </header>
        <main id="app-main" className="flex-1 overflow-y-auto p-4 pb-24 md:p-6 md:pb-6">{children}</main>

        <nav
          className="fixed inset-x-0 bottom-0 z-50 flex border-t border-slate-200 bg-white md:hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <button
            type="button"
            onClick={() => onNavigate('home')}
            className="flex flex-1 flex-col items-center gap-0.5 px-1 py-2 text-[11px] text-slate-600 active:bg-slate-100"
          >
            <span className="text-xl">🏠</span> Inicio
          </button>
          <button
            type="button"
            onClick={() => onNavigate('activity')}
            className="flex flex-1 flex-col items-center gap-0.5 px-1 py-2 text-[11px] text-slate-600 active:bg-slate-100"
          >
            <span className="text-xl">📊</span> Actividad
          </button>
          <button
            type="button"
            onClick={() => onNavigate('apps')}
            className="flex flex-1 flex-col items-center gap-0.5 px-1 py-2 text-[11px] text-slate-600 active:bg-slate-100"
          >
            <span className="text-xl">📱</span> Apps
          </button>
          {onDownloads && (
            <button
              type="button"
              onClick={onDownloads}
              className="flex flex-1 flex-col items-center gap-0.5 px-1 py-2 text-[11px] text-brand-700 active:bg-slate-100"
            >
              <span className="text-xl">📥</span> Descargas
            </button>
          )}
          {onAccount && (
            <button
              type="button"
              onClick={onAccount}
              className="flex flex-1 flex-col items-center gap-0.5 px-1 py-2 text-[11px] text-slate-600 active:bg-slate-100"
            >
              <span className="text-xl">👤</span> Cuenta
            </button>
          )}
        </nav>
      </div>
    </div>
  )
}
